// Subscription / onboarding gating, extracted verbatim from App.tsx so the
// rules that decide whether a paying customer sees the dashboard, the paywall,
// or the signup wizard are (a) in one place and (b) unit-testable. Every
// function here is pure — no hooks, no context, no I/O.

/** Plan tiers that mean "this row is paid for". `plan_tier` defaults to 'free'
 *  (NOT in this list), so it never lets a genuinely-free brand through. */
export const PAID_TIERS = ['starter', 'pro', 'pro_annual', 'agency'] as const

/** Plan statuses that still grant access. 'canceled' is deliberately included:
 *  a cancelled subscription keeps working until the period actually ends (the
 *  period-end webhook is what clears whop_subscription_id). */
const ACTIVE_STATUSES = ['active', 'trialing', 'canceled']

export interface SubscribableRow {
  whop_subscription_id?: string | null
  plan_tier?: string | null
  plan_status?: string | null
  onboarding_completed?: boolean | null
}

export interface AccountLike extends SubscribableRow {
  type?: string | null
}

/** True when a brand row is covered by a subscription of its own or a paid tier. */
export function brandIsSubscribed(b: SubscribableRow | null | undefined): boolean {
  return !!b?.whop_subscription_id || (PAID_TIERS as readonly string[]).includes(b?.plan_tier ?? '')
}

/**
 * Whether the Hiring section is unlocked.
 *
 * Access is determined at the ACCOUNT level (spec §6). A brand under an active
 * AGENCY subscription is covered by that subscription — its own brand_configs
 * row carries no individual sub. A solo 'brand' account reads the brand's own
 * subscription, exactly as before agencies existed.
 *
 * Checks whop_subscription_id (or a paid tier), NOT plan_status alone:
 * brand_configs.plan_status defaults to 'active' at row creation, so status
 * alone would let every free user through.
 */
export function hasHiringAccess(
  brand: SubscribableRow | null | undefined,
  account: AccountLike | null | undefined,
): boolean {
  const resolved = account?.type === 'agency'
    ? { id: account.whop_subscription_id, status: account.plan_status, tier: account.plan_tier }
    : { id: brand?.whop_subscription_id, status: brand?.plan_status, tier: brand?.plan_tier }
  const statusOk = ACTIVE_STATUSES.includes(resolved.status ?? '')
  return (!!resolved.id || (PAID_TIERS as readonly string[]).includes(resolved.tier ?? '')) && statusOk
}

/**
 * Whether to show the signup wizard instead of the dashboard.
 *
 * `anchorBrand` must be the ANCHOR brand (useBrandLoader's), not the effective
 * one: useEffectiveBrand masks the effective brand to null for an agency at the
 * lobby, and the agency branch below needs to see that the user already owns a
 * real, set-up brand.
 *
 * The `ownsSetUpBrand` term is a safety net for stranded-account data (see
 * pickAccount): an abandoned $997 agency signup leaves a brandless agency
 * account with onboarding_completed=false and no sub, and if that is the
 * account we resolve, the agency branch would send a fully-paid brand owner
 * back to step 1 with no way out.
 */
export function needsOnboarding(opts: {
  account: AccountLike | null | undefined
  /** The EFFECTIVE brand (what the user is looking at). */
  brand: SubscribableRow | null | undefined
  /** The ANCHOR brand from useBrandLoader. */
  anchorBrand: SubscribableRow | null | undefined
}): boolean {
  const { account, brand, anchorBrand } = opts
  const ownsSetUpBrand =
    !!anchorBrand && (!!anchorBrand.onboarding_completed || brandIsSubscribed(anchorBrand))

  if (account?.type === 'agency') {
    // A paid agency is past onboarding even if the onboarding_completed flag
    // hasn't propagated yet on the checkout return.
    return !account.onboarding_completed && !account.whop_subscription_id && !ownsSetUpBrand
  }
  if (brand) {
    // Likewise, a subscribed brand is past onboarding. Guards the post-embedded-
    // checkout race where the sub lands a tick before the onboarding_completed
    // write reflects.
    return !brand.onboarding_completed && !brandIsSubscribed(brand)
  }
  // No brand resolved -> brand-new user -> onboarding. A FAILED brand load also
  // lands here and must NOT be treated as a new signup; App.tsx catches that
  // case with its retry screen before ever calling this.
  return true
}
